import React, { useEffect, useMemo, useState } from 'react';
import { useAppStore } from '@/store';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { ArrowRight, Check, ChevronRight, FileText, MapPin, UploadCloud } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { TOTAL_WEEKS } from '@/utils/dateUtils';
// Put iet-logo.jpg in src/assets (or change this path)
import ietLogo from '@/assets/iet-logo.jpg';

/* ---------- configuration ---------- */
// Logbook is finished when this many daily reports AND all weekly reports exist.
const REQUIRED_DAILY_REPORTS = 20;
// Where "Continue with Logbook" goes (your weekly reports page).
const REPORTS_ROUTE = '/weekly-report';
// Fallback submission date, used when the API does not send `submission_date`.
const DEFAULT_SUBMISSION_DATE = 'Friday, 6 November 2026';
// Slideshow images shown on the right half of the IET card.
// Drop photos in /public/slides and list them here. Missing files just show the red tint.
const SLIDE_IMAGES = ['/slides/slide-1.png', '/slides/slide-2.png', '/slides/slide-3.png', '/slides/slide-4.png', '/slides/slide-5.png'];
const SLIDE_INTERVAL_MS = 4500;

type StepState = 'done' | 'current' | 'upcoming';

const formatDate = (value?: string) => {
  if (!value) return DEFAULT_SUBMISSION_DATE;
  const d = new Date(value);
  return isNaN(d.getTime())
    ? value
    : d.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
};

export const DashboardPage: React.FC = () => {
  const { dashboardStats, fetchDashboard, loading, profile, dailyReports, weeklyReports, fetchDailyReports, fetchWeeklyReports } =
    useAppStore();
  const navigate = useNavigate();

  const safeDailyReports = dailyReports || [];
  const safeWeeklyReports = weeklyReports || [];

  const [showDate, setShowDate] = useState(false);
  const [slide, setSlide] = useState(0);
  const [failed, setFailed] = useState<Record<number, boolean>>({});

  useEffect(() => {
    (async () => {
      try {
        await Promise.all([fetchDashboard(), fetchDailyReports(), fetchWeeklyReports()]);
      } catch (error) {
        console.error('Failed to load dashboard data:', error);
      }
    })();
  }, []);

  useEffect(() => {
    if (SLIDE_IMAGES.length < 2) return;
    const id = window.setInterval(() => setSlide((s) => (s + 1) % SLIDE_IMAGES.length), SLIDE_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, []);

  const logbookDone = useMemo(
    () => safeDailyReports.length >= REQUIRED_DAILY_REPORTS && safeWeeklyReports.length >= TOTAL_WEEKS,
    [safeDailyReports.length, safeWeeklyReports.length]
  );

  if (loading.isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] p-3">
        <LoadingSpinner size="lg" color="primary" message="Loading your dashboard..." />
      </div>
    );
  }

  const displayName =
    dashboardStats?.user?.first_name ||
    profile?.user_details?.full_name?.trim().split(' ')[0] ||
    profile?.user_details?.username ||
    '';

  const ptPlace = [profile?.company_name, profile?.company_region].filter(Boolean).join(', ');
  const submissionDate = formatDate((dashboardStats as any)?.submission_date);

  const steps: { key: string; label: string; sub: string; state: StepState; icon: React.ReactNode }[] = [
    {
      key: 'logbook',
      label: 'Logbook',
      sub: logbookDone ? 'Completed' : 'In progress',
      state: logbookDone ? 'done' : 'current',
      icon: logbookDone ? <Check className="w-5 h-5" strokeWidth={3} /> : <ArrowRight className="w-5 h-5" />,
    },
    {
      key: 'general',
      label: 'General Report',
      sub: 'Prepare',
      state: logbookDone ? 'current' : 'upcoming',
      icon: logbookDone ? <ArrowRight className="w-5 h-5" /> : <FileText className="w-5 h-5" />,
    },
    { key: 'submission', label: 'Submission', sub: 'Upcoming', state: 'upcoming', icon: <UploadCloud className="w-5 h-5" /> },
  ];

  const dotClass = (s: StepState) =>
    s === 'done'
      ? 'bg-emerald-700 border-emerald-700 text-white'
      : s === 'current'
        ? 'bg-white border-[3px] border-emerald-700 text-emerald-700 ring-4 ring-emerald-100'
        : 'bg-white border-2 border-stone-300 text-stone-500';

  return (
    <div className="p-3 lg:p-6 max-w-7xl mx-auto bg-[#faf7ef]">
      {/* Greeting */}
      <div className="mb-5">
        <h1 className="text-2xl lg:text-3xl font-bold text-[#10243a]">
          Congratulations Eng.{displayName ? ` ${displayName}` : ''}
        </h1>
        <p className="mt-2 text-sm lg:text-base font-semibold text-emerald-700">
          Don't forget to add your PT experience to your CV.
        </p>
        {ptPlace && (
          <p className="mt-2 text-sm text-slate-500 inline-flex items-center gap-1">
            <MapPin className="w-4 h-4" aria-hidden="true" />
            <span>PT Location: {ptPlace}</span>
          </p>
        )}
      </div>

      {/* PT Journey */}
      <section className="rounded-2xl bg-[#fffdf8] p-4 lg:p-6 shadow-sm">
        <h2 className="text-xl lg:text-2xl font-semibold text-[#10243a]">Your PT Journey</h2>
        <p className="text-sm text-slate-500 mb-5">From experience to your professional record.</p>

        <div className="flex items-start">
          {steps.map((s, i) => {
            const body = (
              <>
                <div className={`mx-auto h-[42px] w-[42px] rounded-full grid place-items-center ${dotClass(s.state)}`}>{s.icon}</div>
                <p className={`mt-2 text-[13px] ${s.state === 'current' ? 'font-bold' : 'font-medium'} text-[#10243a]`}>{s.label}</p>
                <p className="text-xs text-slate-500">{s.sub}</p>
              </>
            );
            return (
              <React.Fragment key={s.key}>
                {s.key === 'submission' ? (
                  <button
                    type="button"
                    onClick={() => setShowDate((v) => !v)}
                    aria-expanded={showDate}
                    className="w-24 text-center cursor-pointer"
                  >
                    {body}
                  </button>
                ) : (
                  <div className="w-24 text-center">{body}</div>
                )}
                {i < steps.length - 1 && (
                  <div className={`mt-5 h-0.5 flex-1 ${s.state === 'done' ? 'bg-emerald-600/60' : 'bg-stone-200'}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {showDate && (
          <div className="mt-4 rounded-xl border border-stone-200 bg-white px-4 py-3 text-sm text-[#10243a]">
            Submission date: <span className="font-semibold text-emerald-700">{submissionDate}</span>
          </div>
        )}

        {logbookDone ? (
          <div className="mt-5 flex w-full items-center gap-3 rounded-xl border border-[#d5e0cf] bg-[#e9f0e3] px-4 py-3 text-base font-medium text-emerald-800">
            <Check className="w-5 h-5" aria-hidden="true" />
            <span className="flex-1">Congratulations on finishing your logbook!</span>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => navigate(REPORTS_ROUTE)}
            className="mt-5 flex w-full items-center gap-3 rounded-xl border border-[#d5e0cf] bg-[#e9f0e3] px-4 py-3 text-left text-base font-medium text-emerald-800 hover:bg-[#e0ebd9] transition-colors"
          >
            <FileText className="w-5 h-5" aria-hidden="true" />
            <span className="flex-1">Continue with Logbook</span>
            <ChevronRight className="w-5 h-5" aria-hidden="true" />
          </button>
        )}
      </section>

      {/* IET UDSM card: full width, edge to edge */}
      <section className="relative mt-5 -mx-3 lg:-mx-6 overflow-hidden bg-gradient-to-b from-[#fbf3ec] to-[#f1d9cb] min-h-[270px]">
        {/* slideshow on the right half, fades into the card on every side */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 right-0 w-[58%] lg:w-1/2"
          style={{
            WebkitMaskImage: 'linear-gradient(to right, transparent 0%, #000 60%)',
            maskImage: 'linear-gradient(to right, transparent 0%, #000 60%)',
          }}
        >
          {SLIDE_IMAGES.map((src, i) => (
            <img
              key={src}
              src={src}
              alt=""
              onError={() => setFailed((f) => ({ ...f, [i]: true }))}
              className="absolute inset-0 h-full w-full object-cover transition-all duration-[1600ms] ease-out"
              style={{
                opacity: i === slide && !failed[i] ? 1 : 0,
                transform: i === slide ? 'scale(1)' : 'scale(1.04)',
                transitionProperty: 'opacity, transform',
                transitionDuration: '1600ms',
                transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            />
          ))}
          {/* layers above the images */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#b3122b]/35 via-[#8a0c20]/20 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#f4e4d9]/80 via-transparent via-25% to-[#f1d9cb]/90" style={{ backgroundSize: '100% 100%' }} />
        </div>

        <div className="relative z-10 px-5 py-6 lg:px-8 lg:py-8 max-w-[64%] lg:max-w-[52%]">
          {/* Transparent overlay layer behind text for better readability - no harsh boundary */}
          <div className="absolute -left-3 -top-3 -bottom-3 right-2 rounded-3xl bg-gradient-to-r from-white/60 via-white/30 to-transparent backdrop-blur-[0.5px] -z-10" />
          <div className="relative flex items-center gap-3">
            <img src={ietLogo} alt="Institution of Engineers Tanzania" className="h-14 w-14 rounded-full bg-white object-contain shadow border border-white/60" />
            <div className="leading-tight">
              <p className="text-sm font-extrabold text-[#b3122b] drop-shadow-sm">IET Tanzania</p>
              <p className="text-xs text-slate-800 font-medium drop-shadow-sm">UDSM Student Chapter</p>
            </div>
          </div>
          <p className="mt-3 font-serif italic text-xl text-[#8a0c20]">Being That Engineer</p>

          <h3 className="mt-4 text-xl lg:text-2xl font-bold text-[#a0522d] leading-tight drop-shadow-sm">
            Engineering isn't only what you learn in class.<br />
            It's what you become when theory meets the real world.
          </h3>
          <p className="mt-2 text-base lg:text-lg text-slate-800">
            Your practical training gave you the experience.<br />
            Your reports tell the story.
          </p>
          <p className="mt-3 text-xs lg:text-sm text-slate-600">Be That Engineer</p>

          <a
            href="https://ietudsm.co.tz"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 inline-flex items-center gap-2 rounded-full border-[1.5px] border-[#b3122b] bg-white/80 px-4 py-2 text-sm font-medium text-[#8a0c20] shadow-sm hover:bg-white"
          >
            Explore IET UDSM →
            <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </a>
        </div>
      </section>

      {/* Recent weekly reports only */}
      <section className="mt-6 rounded-2xl bg-[#fffdf8] p-4 lg:p-5 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base lg:text-lg font-semibold text-[#10243a]">Recent Weekly Reports</h3>
          <button
            type="button"
            onClick={() => navigate('/weekly-report')}
            className="text-sm font-medium text-[#b3122b] hover:text-[#8a0c20]"
          >
            View All
          </button>
        </div>

        <div className="space-y-2">
          {safeWeeklyReports.slice(0, 3).map((report) => (
            <button
              key={report.id}
              type="button"
              onClick={() => navigate(`/weekly-report/${report.week_number}`)}
              className="w-full text-left flex items-center gap-3 p-3 rounded-xl bg-stone-50 hover:bg-stone-100 transition-colors"
            >
              <span className="h-2 w-2 rounded-full bg-emerald-600 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#10243a]">Week {report.week_number}</p>
                <p className="text-xs text-slate-600 truncate">{report.summary?.substring(0, 60) || 'No summary'}...</p>
              </div>
              <span
                className={`text-xs px-2 py-1 rounded flex-shrink-0 ${
                  report.status === 'SUBMITTED'
                    ? 'bg-green-100 text-green-800'
                    : report.status === 'APPROVED'
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-gray-100 text-gray-800'
                }`}
              >
                {report.status || 'DRAFT'}
              </span>
            </button>
          ))}
          {safeWeeklyReports.length === 0 && (
            <p className="text-slate-500 text-center py-4 text-sm">No weekly reports yet</p>
          )}
        </div>
      </section>
    </div>
  );
};
